import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import apiService from '../../services/api.service'
import { getUserData, updateUserPreferences } from '../../services/auth.service'
import { Finca, UploadStatus, FincasResponse, ImageUploadResponse, UserApiResponse, MessagePreference } from '../../types'
import { useRandomFrase } from '../../hooks/useRandomFrase'
import './menu.css'

const Menu: React.FC = () => {
  const [fincas, setFincas] = useState<Finca[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({ loading: false, error: null, success: false });
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState<string>(
    localStorage.getItem('categoriaMensaje') || 'Referencias bíblicas'
  );
  const [selectedPreference, setSelectedPreference] = useState<MessagePreference>('fe');
  const [userData, setUserData] = useState<UserApiResponse | null>(null);
  const [userLoading, setUserLoading] = useState<boolean>(true);
  const [savingPreferences, setSavingPreferences] = useState<boolean>(false);
  const API_URL = process.env.REACT_APP_API_URL as string;
  const fraseAleatoria = useRandomFrase(undefined, userData?.user?.messages_pref);

  // Opciones de preferencias para la API (con etiquetas para mostrar al usuario)
  const messagePreferences: { value: MessagePreference, label: string }[] = [
    { value: 'fe', label: 'Referencias bíblicas' },
    { value: 'motivacional', label: 'Motivación diaria' },
    { value: 'paciencia', label: 'Paciencia y cosecha' },
    { value: 'fortaleza', label: 'Fortaleza y perseverancia' },
    { value: 'gratitud', label: 'Gratitud y bendición' },
    { value: 'negocios', label: 'Sabiduría y administración' },
    { value: 'disciplina', label: 'Enfoque y disciplina' }
  ];

  useEffect(() => {
    fetchFincas();
    fetchUserData();
  }, []);

  // Función para cargar los datos del usuario
  const fetchUserData = async (): Promise<void> => {
    try {
      setUserLoading(true);
      const data = await getUserData();
      setUserData(data);
      // Establecer la preferencia actual del usuario
      setSelectedPreference(data.user.messages_pref);
    } catch (err) {
      console.error('Error al cargar datos del usuario:', err);
      // Si falla, continuar con el funcionamiento normal
    } finally {
      setUserLoading(false);
    }
  };

  // Función para manejar la selección de preferencia
  const handlePreferenceChange = (preference: MessagePreference): void => {
    setSelectedPreference(preference);
  };

  // Función para guardar la selección usando la API
  const handleSaveSelection = async (): Promise<void> => {
    try {
      setSavingPreferences(true);
      
      // Guardar en la API
      await updateUserPreferences({ messages_pref: selectedPreference });
      
      // Actualizar los datos del usuario localmente
      if (userData) {
        setUserData({
          ...userData,
          user: {
            ...userData.user,
            messages_pref: selectedPreference
          }
        });
      }
      
      // Cerrar el modal
      setIsCategoryModalOpen(false);
      
      // Mostrar mensaje de éxito (opcional)
      console.log('Preferencias actualizadas correctamente');
      
      // Ya no es necesario recargar la página porque el hook useRandomFrase
      // se actualizará automáticamente con la nueva preferencia
      
    } catch (error) {
      console.error('Error al guardar preferencias:', error);
      // Opcional: Mostrar mensaje de error al usuario
      alert('Error al guardar las preferencias. Por favor, inténtelo de nuevo.');
    } finally {
      setSavingPreferences(false);
    }
  };

  // Función para abrir el modal
  const handleOpenModal = (): void => {
    // Asegurar que la selección temporal coincida con la preferencia actual del usuario
    if (userData?.user?.messages_pref) {
      setSelectedPreference(userData.user.messages_pref);
    }
    setIsCategoryModalOpen(true);
  };

  // Función para cerrar el modal
  const handleCloseModal = (): void => {
    setIsCategoryModalOpen(false);
    // Resetear la selección temporal a la preferencia actual cuando se cancele
    if (userData?.user?.messages_pref) {
      setSelectedPreference(userData.user.messages_pref);
    }
  };

  const fetchFincas = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await apiService.get<FincasResponse>('/api/fincas');
      setFincas(response.data.fincas);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching fincas:', err);
      setError('Error al cargar las fincas. Por favor, intente de nuevo más tarde.');
      setLoading(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>, finca: Finca): Promise<void> => {
    event.stopPropagation();
    const file = event.target.files?.[0];
    
    if (!file) return;
    
    // Check if file is an image
    if (!file.type.match('image.*')) {
      setUploadStatus({
        loading: false,
        error: 'Solo se permiten archivos de imagen (png, jpg, jpeg, gif).',
        success: false
      });
      return;
    }
    
    try {
      setUploadStatus({ loading: true, error: null, success: false });
      
      // Create form data
      const formData = new FormData();
      formData.append('name', finca.nombre);
      formData.append('image', file);
      
      // Upload image
      const response = await apiService.post<ImageUploadResponse>('/api/fincas/imagen', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Handle success
      setUploadStatus({ loading: false, error: null, success: true });
      
      // Update fincas state with new image URL
      setFincas(prevFincas => 
        prevFincas.map(f => 
          f.nombre === finca.nombre 
            ? { ...f, imagen: response.data.imagen_url.split('/').pop() } 
            : f
        )
      );
      
      // Show success message
      setTimeout(() => {
        setUploadStatus({ loading: false, error: null, success: false });
      }, 3000);
      
    } catch (err: any) {
      console.error('Error uploading image:', err);
      setUploadStatus({
        loading: false, 
        error: err.response?.data?.message || 'Error al subir la imagen. Por favor, intente de nuevo.',
        success: false
      });
    }
  };

  // Simplified helper function to render a finca item with consistent styling
  const renderFincaItem = (finca: Finca, index: number): React.ReactElement => {
    // Construct the absolute image URL
    const imageUrl = finca.imagen ? `${API_URL}/api/images/${finca.imagen}` : null;
    
    // Card background style
    const cardBackgroundStyle = imageUrl ? {
      backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(${imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat'
    } : {};
    
    return (
      <div 
        key={index} 
        className="menu-container13 upload-container2"
        style={cardBackgroundStyle}
      >
        <div className="menu-container14">
          {/* Image upload button */}
          <div className="image-upload-box2" onClick={() => document.getElementById(`imageInput-${index}`)?.click()}>
            <button className="btn-3d-upload">
              Nueva Foto
            </button>
            <input
              type="file"
              id={`imageInput-${index}`}
              accept="image/*"
              hidden
              onChange={(e) => handleImageUpload(e, finca)}
              className="menu-textinput1 input image-input2"
            />
          </div>
          
          {/* Finca info and tipos on the right */}
          <div className="finca-info-section">
            <h3 className="finca-nombre">{finca.nombre}</h3>
            <div className="tipos-grid">
              {finca.tipos.map((tipo, tipoIndex) => (
                <Link 
                  key={tipoIndex}
                  to={`/proceso-calculo?name=${encodeURIComponent(finca.nombre)}&tipo=${encodeURIComponent(tipo)}`}
                  className="tipo-button"
                >
                  {tipo}
                </Link>
              ))}
            </div>
            {/* Botón Consolidado Finca - Solo si hay más de un tipo */}
            {finca.tipos.length > 1 && (
              <Link 
                to={`/proceso-calculo?name=${encodeURIComponent(finca.nombre)}&tipo=consolidado`}
                className="tipo-button consolidado-button"
              >
                Consolidado Finca
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="menu-page">
      <Helmet>
        <title>Menu - ProyectoGanando</title>
        <meta property="og:title" content="Dashboard - ProyectoGanando" />
      </Helmet>
      
      <div className="menu-header">
        <h1 className="menu-title">
          Hola {userData?.user?.name || 'Usuario'}
        </h1>
        <p className="menu-subtitle">
          Gestiona y visualiza el desempeño de tus fincas
        </p>
        {fraseAleatoria && (
          <div className="menu-frase-inspiradora">
            <button 
              className="category-selector-btn"
              onClick={handleOpenModal}
              title="Seleccionar tipo de mensaje"
            >
              (*)
            </button>
            <p className="frase-texto">
              "{fraseAleatoria.frase}"
            </p>
            <p className="frase-cita">
              {fraseAleatoria.tipoReferencia} {fraseAleatoria.cita}
            </p>
          </div>
        )}
      </div>

      <div className="menu-content">
        {loading ? (
          <div className="menu-loading">
            <div className="menu-loading-spinner">
              <img src="/logocabeza.svg" alt="Logo" />
            </div>
            <p>Cargando fincas...</p>
          </div>
        ) : error ? (
          <div className="menu-error">
            <p>{error}</p>
          </div>
        ) : (
          <div className="menu-fincas-grid">
            {fincas.map((finca, index) => renderFincaItem(finca, index))}
          </div>
        )}
      </div>

      {/* Modal de selección de categorías */}
      {isCategoryModalOpen && (
        <div 
          className="category-modal-overlay" 
          onClick={handleCloseModal}
        >
          <div 
            className="category-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Botón cerrar */}
            <button
              onClick={handleCloseModal}
              className="category-modal-close-btn"
            >
              ×
            </button>
            
            {/* Contenido del modal */}
            <div className="category-modal-header">
              <h3>Selecciona el tipo de mensaje que prefieres</h3>
            </div>
            
            <div className="category-modal-body">
              {messagePreferences.map((preference) => (
                <label key={preference.value} className="category-option">
                  <input
                    type="radio"
                    name="message-preference"
                    checked={selectedPreference === preference.value}
                    onChange={() => handlePreferenceChange(preference.value)}
                    disabled={savingPreferences}
                  />
                  <span className="category-label">{preference.label}</span>
                </label>
              ))}
            </div>
            
            <div className="category-modal-footer">
              <button 
                onClick={handleSaveSelection}
                className="save-selection-btn"
                disabled={savingPreferences}
              >
                {savingPreferences ? 'Guardando...' : 'Guardar Elección'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Menu
